import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, AvatarBadge, StatusBadge, PillTag, ImportWizard } from './ui';
import {
  useC360List, C360Table, ColumnChooser, MultiGroupPicker,
  KpiCard, DonutCard, unwrapLookupItems, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { RepresentativeGroupsPage } from './RepresentativeGroupsPage';
import { RepresentativeTypesPage } from './RepresentativeTypesPage';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import './RepresentativesPage.css';

const API_BASE = '/api/representatives';

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label }));

const GROUP_OPTIONS = [
  { value: 'vendorId', label: 'Distribütör' },
  { value: 'groupId', label: 'Grup' },
  { value: 'salesManagerId', label: 'Satış Müdürü' },
  { value: 'status', label: 'Durum' },
  { value: 'representativeTypeId', label: 'Temsilci Tipi' },
];

const GROUP_BY_ODATA = {
  vendorId: { field: 'VendorId', type: 'guid' },
  groupId: { field: 'GroupId', type: 'guid' },
  salesManagerId: { field: 'SalesManagerId', type: 'guid' },
  status: { field: 'Status', type: 'number' },
  representativeTypeId: { field: 'RepresentativeTypeId', type: 'guid' },
};

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  name: { type: 'text', odataField: 'Name' },
  vendorLabel: { type: 'select', odataField: 'VendorId', valueFormat: 'guid' },
  typeLabel: { type: 'select', odataField: 'RepresentativeTypeId', valueFormat: 'guid' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
  // #753 (2. tur) LİSTE GÖRÜNÜMÜNDE kullanıcı arama. Talep: "Kullanıcı alanı diğer rehberlerdeki
  // gibi arama özelliği olmalı; 700-800 kullanıcı olduğunda düz liste iş görmez." Kartın seçicisi
  // aramalı yapılmıştı ama LİSTE kolonunun hiç filtresi yoktu.
  //
  // Kolon türetilmiştir (değer temsilcide değil, temsilci→kullanıcı eşleşmesinde durur), bu yüzden
  // `Id` üzerinden daraltılır: seçilen kullanıcının bağlı olduğu temsilcinin Id'si. Böylece filtre
  // SUNUCU tarafında çalışır — yalnız açık sayfayı süzen istemci filtresi, sayfalı listede
  // "kayıt yok" gibi yanlış sonuç verirdi.
  userEmail: { type: 'asyncSelect', odataField: 'Id', valueFormat: 'guid' },
};

const SEARCH_FIELDS = ['TextId', 'Name', 'ErpId', 'Reference'];

const definitionTabs = [
  { key: 'representatives', label: 'Temsilciler', icon: 'idcard' },
  { key: 'groups', label: 'Temsilci Grubu', icon: 'grid' },
  { key: 'types', label: 'Temsilci Tipi', icon: 'briefcase' },
];
const tabStyles = {
  headerRow: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  tabBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  tab: { minHeight: 36, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #fff)', color: 'var(--text-secondary, #4b5563)', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 7 },
  tabActive: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
};

// Sağ üst sayı kartları (mevcut sayfa tasarımına uygun, sade)
const statStyles = {
  row: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  card: { minWidth: 84, padding: '8px 16px', borderRadius: 12, textAlign: 'center', background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)' },
  value: { fontSize: 20, fontWeight: 800, lineHeight: 1, color: 'var(--text-primary, #0f172a)' },
  valueOk: { color: '#15803d' },
  valueNeutral: { color: 'var(--text-secondary, #64748b)' },
  label: { marginTop: 4, fontSize: 11, fontWeight: 700, color: 'var(--text-secondary, #64748b)' },
};

// ── Kart görünümü ikonları (tasarım destesinden) ──
const REP_ICONS = {
  grid: <><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></>,
  list: <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3.5" y1="6" x2="3.6" y2="6" /><line x1="3.5" y1="12" x2="3.6" y2="12" /><line x1="3.5" y1="18" x2="3.6" y2="18" /></>,
  kanban: <><rect x="3" y="4" width="5" height="13" rx="1.2" /><rect x="10" y="4" width="5" height="9" rx="1.2" /><rect x="17" y="4" width="4" height="16" rx="1.2" /></>,
  bars: <><line x1="4" y1="20" x2="4" y2="12" /><line x1="10" y1="20" x2="10" y2="5" /><line x1="16" y1="20" x2="16" y2="9" /><line x1="22" y1="20" x2="2" y2="20" /></>,
  building: <><rect x="4" y="3" width="16" height="18" rx="1.5" /><path d="M9 21v-4h6v4" /><path d="M8 7h.01M16 7h.01M8 11h.01M16 11h.01" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M5 21a7 7 0 0 1 14 0" /></>,
  tag: <><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 2.8 12V4.8A2 2 0 0 1 4.8 2.8H12a2 2 0 0 1 1.4.6l7.2 7.2a2 2 0 0 1 0 2.8Z" /><circle cx="7.5" cy="7.5" r="1.3" /></>,
  briefcase: <><rect x="2.5" y="7" width="19" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="2.5" y1="12" x2="21.5" y2="12" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></>,
  edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" /></>,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  idcard: <><rect x="2.5" y="5" width="19" height="14" rx="2" /><circle cx="8" cy="11" r="2.2" /><path d="M4.8 16a3.4 3.4 0 0 1 6.4 0" /><line x1="14" y1="10" x2="18.5" y2="10" /><line x1="14" y1="14" x2="17.5" y2="14" /></>,
};
function RIcon({ name, size = 16 }) {
  const path = REP_ICONS[name];
  if (!path) return null;
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{path}</svg>;
}

// Temsilci tipi etiketine göre tutarlı (deterministik) renk üretir.
const TYPE_PALETTE = ['#2F7FD1', '#7A4FD1', '#1E9E7A', '#D9822B', '#0EA5E9', '#e2445c', '#14B8A6', '#94A3B8'];
const typeColor = (label) => {
  const str = String(label || '');
  if (!str) return TYPE_PALETTE[0];
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return TYPE_PALETTE[h % TYPE_PALETTE.length];
};
const repInitials = (name) => String(name || '').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '—';

// Tek temsilci kartı (tasarım destesindeki RepCard)
function RepCard({ r, vendorLabel, typeLabel, userLabel, routeLabel, onView, onEdit, onDel, canEdit = true, canDelete = true }) {
  const col = typeColor(typeLabel);
  const meta = STATUS_META[r.status];
  const isActive = Number(r.status) === 1;
  return (
    <div className="rep-card">
      <div className="rep-card-band" style={{ background: `linear-gradient(120deg, ${col}, ${col}cc)` }}>
        <span className="rep-card-ava">{repInitials(r.name)}</span>
        <span className={'rep-st ' + (isActive ? '' : 'off')}><span className="dot" />{meta?.label || '—'}</span>
      </div>
      <div className="rep-card-id">
        <div className="rep-card-name" title={r.name || ''}>{r.name || '-'}</div>
        <div className="rep-card-code">{r.textId || '-'}{r.erpId ? ` · ${r.erpId}` : ''}</div>
      </div>
      <div className="rep-card-rows">
        <div className="rep-crow"><RIcon name="building" size={15} /><span>{vendorLabel && vendorLabel !== '-' ? vendorLabel : 'Distribütör yok'}</span></div>
        <div className="rep-crow"><RIcon name="idcard" size={15} /><span>{userLabel && userLabel !== '-' ? userLabel : 'Kullanıcı yok'}</span></div>
        <div className="rep-crow"><RIcon name="list" size={15} /><span>{routeLabel && routeLabel !== '-' ? routeLabel : 'Rut atanmadı'}</span></div>
      </div>
      <div className="rep-card-foot">
        <span className="rep-typechip" style={{ color: col, background: col + '18' }}><RIcon name="briefcase" size={13} /> {typeLabel || '—'}</span>
        <div className="rep-cacts">
          <button type="button" title="Görüntüle" onClick={onView}><RIcon name="eye" size={15} /></button>
          {canEdit && <button type="button" title="Düzenle" onClick={onEdit}><RIcon name="edit" size={15} /></button>}
          {canDelete && <button type="button" className="del" title="Sil" onClick={onDel}><RIcon name="trash" size={15} /></button>}
        </div>
      </div>
    </div>
  );
}

// === Temsilci Grafik görünümü ===
function ChartView({ rows, getGroupLabel, getVendorLabel, getTypeLabel }) {
  const total = rows.length;
  const active = rows.filter((r) => Number(r.status) === 1).length;
  const groupSet = new Set(rows.map((r) => r.groupId).filter(Boolean));
  const vendorSet = new Set(rows.map((r) => r.vendorId).filter(Boolean));
  const now = new Date();
  const ymCurrent = now.getFullYear() * 100 + (now.getMonth() + 1);
  const newThisMonth = rows.filter((r) => {
    if (!r.createdDate) return false;
    const d = new Date(r.createdDate);
    if (Number.isNaN(d.getTime())) return false;
    return d.getFullYear() * 100 + (d.getMonth() + 1) === ymCurrent;
  }).length;

  const groupCounts = {};
  rows.forEach((r) => {
    const lbl = r.groupId ? getGroupLabel(r.groupId) : 'Atanmamış';
    groupCounts[lbl] = (groupCounts[lbl] || 0) + 1;
  });
  const groupData = Object.entries(groupCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 12);

  const statusPalette = { ok: '#00c875', neutral: '#94a3b8', wait: '#fdab3d' };
  const statusCounts = {};
  rows.forEach((r) => {
    const k = r.status == null || r.status === '' ? '__none__' : Number(r.status);
    statusCounts[k] = (statusCounts[k] || 0) + 1;
  });
  const statusData = Object.entries(statusCounts).map(([k, v]) => {
    const meta = STATUS_META[k];
    return { name: meta?.label || 'Bilinmiyor', value: v, color: meta ? statusPalette[meta.tone] : '#cbd5e1' };
  });

  const wtCounts = {};
  rows.forEach((r) => {
    const lbl = r.representativeTypeId ? getTypeLabel(r.representativeTypeId) : 'Atanmamış';
    wtCounts[lbl] = (wtCounts[lbl] || 0) + 1;
  });
  const wtPalette = ['#0073ea', '#00c875', '#a855f7', '#fdab3d', '#0ea5e9', '#e2445c', '#14b8a6', '#94a3b8'];
  const wtData = Object.entries(wtCounts).map(([name, value], i) => ({ name, value, color: wtPalette[i % wtPalette.length] })).sort((a, b) => b.value - a.value).slice(0, 8);

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Toplam Temsilci" value={total.toLocaleString('tr-TR')} />
        <KpiCard label="Aktif Temsilci" value={active.toLocaleString('tr-TR')} hint={total > 0 ? `%${((active / total) * 100).toFixed(0)} aktif` : '—'} />
        <KpiCard label="Grup" value={groupSet.size.toLocaleString('tr-TR')} hint="farklı grup" />
        <KpiCard label="Distribütör" value={vendorSet.size.toLocaleString('tr-TR')} hint="farklı distribütör" />
        <KpiCard label="Bu Ay Eklenen" value={newThisMonth.toLocaleString('tr-TR')} hint="Yeni temsilci" />
      </div>

      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Gruba Göre Dağılım</span>
          <span className="c360-chart-panel-sub">İlk {groupData.length} grup</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={groupData} margin={{ top: 12, right: 8, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-25} textAnchor="end" interval={0} height={60} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }} formatter={(v) => [`${v} temsilci`, 'Adet']} />
            <Bar dataKey="value" fill="#0ea5e9" radius={[8, 8, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="c360-chart-donuts">
        <DonutCard title="Durum Dağılımı" subtitle={`${total} temsilci`} data={statusData} />
        <DonutCard title="Temsilci Tipi" subtitle={`İlk ${wtData.length}`} data={wtData} />
      </div>
    </div>
  );
}

export function RepresentativesPage() {
  usePageMeta('Temsilciler', 'Temsilci kayıtlarını yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const [activeDefinition, setActiveDefinition] = useState('representatives');

  const [vendors, setVendors] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  // 901: temsilci -> bağlı Kullanıcı eşleşmeleri (representativeId ile map'lenir).
  const [userByRep, setUserByRep] = useState({});
  // 801: temsilci -> bağlı olduğu (ilk) Rut Planı adı (representativeId ile map'lenir).
  const [routeByRep, setRouteByRep] = useState({});

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  // Sağ üst sayı kartları için sunucudan çekilen veriler (sekmeye göre kullanılır)
  const [repsAll, setRepsAll] = useState([]);
  const [typesAll, setTypesAll] = useState([]);
  // Görünüm: card | table | kanban | chart (kart varsayılan; kart, tablo verisini + scroll'da loadMore kullanır)
  const [view, setView] = useState('card');

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const getVendorLabel = useCallback((id) => {
    const v = vendors.find((item) => item.id === id);
    if (!v) return '-';
    return v.name ? `${v.textId || ''} - ${v.name}`.trim().replace(/^- /, '') : v.textId || '-';
  }, [vendors]);
  const getGroupLabel = useCallback((id) => {
    const g = groups.find((item) => item.id === id);
    if (!g) return '-';
    return g.description ? `${g.textId || ''} - ${g.description}`.trim().replace(/^- /, '') : g.textId || '-';
  }, [groups]);
  const getSalesManagerLabel = useCallback((id) => {
    const m = salesManagers.find((item) => item.id === id);
    if (!m) return '-';
    return m.description ? `${m.textId || ''} - ${m.description}`.trim().replace(/^- /, '') : m.textId || '-';
  }, [salesManagers]);
  const getTypeLabel = useCallback((id) => {
    const t = typesAll.find((item) => item.id === id);
    if (!t) return '-';
    const name = t.name || (t.code != null ? `#${t.code}` : '');
    return t.code != null && t.code !== '' && name ? `${t.code} - ${name}` : (name || '-');
  }, [typesAll]);
  // 901: temsilcinin bağlı olduğu kullanıcının etiketi (Ad + email). Yoksa '-'.
  const getUserLabel = useCallback((repId) => {
    const u = userByRep[String(repId)];
    if (!u) return '-';
    const name = u.fullName || u.email || (u.registerNo ? `#${u.registerNo}` : '');
    return u.email && name && u.email !== name ? `${name} (${u.email})` : (name || '-');
  }, [userByRep]);
  // 801: temsilcinin bağlı olduğu kullanıcının SADECE e-posta adresi (liste sütunu). Yoksa '-'.
  const getUserEmail = useCallback((repId) => {
    const u = userByRep[String(repId)];
    return (u && u.email) ? u.email : '-';
  }, [userByRep]);
  // 801: temsilcinin bağlı olduğu (ilk) rut planı adı (liste sütunu). Yoksa '-'.
  const getRouteLabel = useCallback((repId) => routeByRep[String(repId)] || '-', [routeByRep]);

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/vendors/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/representativegroups/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/salesmanagers/Select', { headers }).then((res) => res.ok ? res.json() : []),
    ]).then(([vendorData, groupData, salesManagerData]) => {
      setVendors(unwrapLookupItems(vendorData));
      setGroups(unwrapLookupItems(groupData));
      setSalesManagers(unwrapLookupItems(salesManagerData));
    }).catch(() => {}).finally(() => setLookupsLoaded(true));
  }, []);

  // Sayı kartları verisi (Temsilciler & Tip sayıları sunucudan; gruplar lookup'tan)
  const loadStatsData = useCallback(() => {
    const headers = authHeaders();
    fetch('/api/representatives/Select', { headers }).then((res) => res.ok ? res.json() : []).then((d) => setRepsAll(unwrapLookupItems(d))).catch(() => {});
    fetch('/api/representativetypes/Select', { headers }).then((res) => res.ok ? res.json() : []).then((d) => setTypesAll(unwrapLookupItems(d))).catch(() => {});
    // 901: temsilci -> kullanıcı eşleşmelerini repId ile map'le (liste "Kullanıcı" sütunu).
    fetch('/api/representatives/user-assignments', { headers })
      .then((res) => res.ok ? res.json() : [])
      .then((d) => {
        const items = unwrapLookupItems(d);
        const map = {};
        (Array.isArray(items) ? items : []).forEach((u) => {
          const rid = u.representativeId ?? u.RepresentativeId;
          if (rid) map[String(rid)] = u;
        });
        setUserByRep(map);
      })
      .catch(() => {});
    // 801: temsilci -> bağlı olduğu rut planı adı. Rut planı başına atanmış temsilciler
    // ayrı endpoint'te; tüm planları çekip her planın temsilcilerini birleştiriyoruz.
    // (Rut planı adedi düşük olduğu için tek seferlik yükte kabul edilebilir.)
    fetch('/api/route-plans', { headers })
      .then((res) => res.ok ? res.json() : null)
      .then(async (d) => {
        const plans = unwrapLookupItems(d);
        const list = Array.isArray(plans) ? plans : [];
        const map = {};
        await Promise.all(list.map(async (p) => {
          const pid = p.id ?? p.Id;
          const pname = p.name ?? p.Name;
          if (!pid) return;
          const reps = await fetch(`/api/route-plans/${pid}/representatives`, { headers })
            .then((rr) => (rr.ok ? rr.json() : null))
            .then((j) => unwrapLookupItems(j))
            .catch(() => []);
          (Array.isArray(reps) ? reps : []).forEach((r) => {
            const rid = r.representativeId ?? r.RepresentativeId;
            if (rid && !map[String(rid)]) map[String(rid)] = pname; // ilk plan
          });
        }));
        setRouteByRep(map);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadStatsData(); }, [loadStatsData]);

  // Aktif sekmeye göre 3 sayı kartı
  const statCards = useMemo(() => {
    const total = repsAll.length;
    const active = repsAll.filter((r) => Number(r.status) === 1).length;
    const passive = repsAll.filter((r) => Number(r.status) === 0).length;
    if (activeDefinition === 'groups') {
      const withGroup = repsAll.filter((r) => r.groupId).length;
      return [
        { label: 'Toplam Grup', value: groups.length },
        { label: 'Gruplu Temsilci', value: withGroup, tone: 'ok' },
        { label: 'Grupsuz Temsilci', value: total - withGroup, tone: 'neutral' },
      ];
    }
    if (activeDefinition === 'types') {
      return [
        { label: 'Toplam Tip', value: typesAll.length },
        { label: 'Toplam Temsilci', value: total },
        { label: 'Aktif Temsilci', value: active, tone: 'ok' },
      ];
    }
    return [
      { label: 'Toplam', value: total },
      { label: 'Aktif', value: active, tone: 'ok' },
      { label: 'Pasif', value: passive, tone: 'neutral' },
    ];
  }, [activeDefinition, repsAll, typesAll, groups]);

  const getKeyLabel = useCallback((field, row) => {
    let rawKey = row[field];
    if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
    const key = field === 'status' ? (rawKey === '__none__' ? '__none__' : Number(rawKey)) : rawKey;
    let label;
    if (key === '__none__') label = 'Atanmamış';
    else if (field === 'status') label = STATUS_META[key]?.label || String(rawKey);
    else if (field === 'vendorId') label = getVendorLabel(key);
    else if (field === 'groupId') label = getGroupLabel(key);
    else if (field === 'salesManagerId') label = getSalesManagerLabel(key);
    else if (field === 'representativeTypeId') label = getTypeLabel(key);
    else label = String(key);
    return { key, label };
  }, [getVendorLabel, getGroupLabel, getSalesManagerLabel, getTypeLabel]);

  const seedKanbanBuckets = useCallback((groupField) => {
    if (groupField === 'status') return Object.entries(STATUS_META).map(([k, meta]) => ({ key: Number(k), label: meta.label }));
    if (groupField === 'vendorId') return vendors.map((v) => ({ key: v.id, label: getVendorLabel(v.id) }));
    if (groupField === 'groupId') return groups.map((g) => ({ key: g.id, label: getGroupLabel(g.id) }));
    if (groupField === 'salesManagerId') return salesManagers.map((m) => ({ key: m.id, label: getSalesManagerLabel(m.id) }));
    if (groupField === 'representativeTypeId') return typesAll.map((t) => ({ key: t.id, label: getTypeLabel(t.id) }));
    return [];
  }, [vendors, groups, salesManagers, typesAll, getVendorLabel, getGroupLabel, getSalesManagerLabel, getTypeLabel]);

  // $select: yalnız görünür kolonların OData alanları çekilir (Column Chooser entegrasyonu).
  // Kart görünümü kolon-görünürlüğünden bağımsız olarak ad/kod/ERP/durum/distribütör/müdür/grup/tip
  // gösterir → bunlar `always`'te. Plaka kolonunun COLUMN_FILTER_META girdisi yok → `always`'te.
  // sortField (textId/name/status/plaka) zaten always'te; needsFullData guard'ı hook içinde.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'Name', 'TextId', 'ErpId', 'Status', 'VendorId', 'SalesManagerId', 'GroupId', 'RepresentativeTypeId', 'Plaka'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    groupByOdata: GROUP_BY_ODATA,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'TextId',
    defaultKanbanGroup: 'status',
    getKeyLabel,
    seedKanbanBuckets,
    selectFields,
    ready: lookupsLoaded,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading, loadingMore,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    viewMode, setViewMode, groupBy, setGroupBy, isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    nestedGroupBuckets, fetchData, loadMore, exportRows,
  } = list;

  // Kart görünümü tablo verisini (sayfalı rows + scroll'da loadMore) kullanır.
  useEffect(() => { setViewMode(view === 'card' ? 'table' : view); }, [view, setViewMode]);

  // Kart ızgarasında aşağı kaydırınca sunucudan sonraki partiyi yükle.
  const handleCardScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) loadMore();
  };

  const openNew = () => navigate('/representatives/new');
  const openEdit = (row = selected) => { if (!row) return; navigate(`/representatives/${row.id}?edit=1`, { state: { row } }); };
  const openView = (row = selected) => { if (!row) return; navigate(`/representatives/${row.id}`, { state: { row, view: true } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) {
        // #782 Sabit "Kayıt silinemedi" yerine SUNUCUNUN GEREKÇESİ. Backend zaten anlaşılır bir
        // metin dönüyor (ör. "Bu temsilciye bağlı 3 alt tüccar var…"); ekran onu yutuyordu ve
        // kullanıcı neyi düzelteceğini bilmiyordu. Detay ekranı bu deseni zaten kullanıyordu.
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      }
      showToast('success', 'Temsilci başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
      loadStatsData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('name', {
      header: 'Ad', meta: { filterType: 'text' },
      cell: (info) => {
        const v = info.getValue();
        if (!v) return '-';
        return <span className="c360-customer-cell"><AvatarBadge name={v} tone="rep" /><span className="c360-cell-main">{v}</span></span>;
      },
    }),
    columnHelper.accessor((row) => getVendorLabel(row.vendorId), {
      id: 'vendorLabel', header: 'Distribütör', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    columnHelper.accessor((row) => getTypeLabel(row.representativeTypeId), {
      id: 'typeLabel', header: 'Temsilci Tipi', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    // 801: Grup/Satış Müdürü/Plaka kaldırıldı; yerine Kullanıcı E-posta + Rut Tanımı eklendi.
    columnHelper.accessor((row) => getUserEmail(row.id), {
      id: 'userEmail', header: 'Kullanıcı E-posta', enableSorting: false, meta: { filterType: 'asyncSelect' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <span className="c360-erp-chip">{v}</span>; },
    }),
    columnHelper.accessor((row) => getRouteLabel(row.id), {
      id: 'routeLabel', header: 'Rut Tanımı', enableSorting: false,
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined || v === '') return '-';
        const meta = STATUS_META[v] ?? { label: String(v), tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [getVendorLabel, getTypeLabel, getUserEmail, getRouteLabel, canEdit, canDelete]);

  const lookupOptions = useMemo(() => ({
    vendorLabel: vendors.map((v) => ({ value: v.id, label: v.name ? `${v.textId || ''} - ${v.name}`.replace(/^- /, '') : (v.textId || v.id) })),
    groupLabel: groups.map((g) => ({ value: g.id, label: g.description ? `${g.textId} - ${g.description}` : (g.textId || g.id) })),
    salesManagerLabel: salesManagers.map((m) => ({ value: m.id, label: m.description ? `${m.textId} - ${m.description}` : (m.textId || m.id) })),
    typeLabel: typesAll.map((t) => ({ value: t.id, label: getTypeLabel(t.id) })),
  }), [vendors, groups, salesManagers, typesAll, getTypeLabel]);

  /**
   * #753 Kullanıcı e-postası filtresinin arama kaynağı.
   *
   * `userByRep` tüm temsilci→kullanıcı eşleşmelerini zaten tutuyor (tek istek, sayı düşük), bu
   * yüzden arama YERELDİR — ayrı bir uç gerekmez. Değer olarak TEMSİLCİ id'si döner; filtre
   * cümlesi `Id eq <guid>` olur (bkz. COLUMN_FILTER_META.userEmail).
   * Sonuç 50 satırla sınırlanır: popover uzun listede kullanılamaz hâle geliyor, kullanıcı yazarak
   * daraltmalı.
   */
  const searchRepUsers = useCallback(async (query) => {
    const term = String(query || '').trim().toLocaleLowerCase('tr-TR');
    return Object.entries(userByRep)
      .map(([repId, u]) => ({
        value: repId,
        label: u?.email || u?.fullName || (u?.registerNo ? `#${u.registerNo}` : repId),
      }))
      .filter((o) => !term || o.label.toLocaleLowerCase('tr-TR').includes(term))
      .sort((a, b) => a.label.localeCompare(b.label, 'tr-TR'))
      .slice(0, 50);
  }, [userByRep]);

  const resolveFilterProps = useCallback((columnId, meta) => {
    if (columnId === 'userEmail') {
      return { fetchOptions: searchRepUsers, resolveLabel: (v) => getUserEmail(v) };
    }
    return { options: meta.options || lookupOptions[columnId] };
  }, [lookupOptions, searchRepUsers, getUserEmail]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('TextId', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Kod' },
        { field: 'name', headerName: 'Ad' },
        { headerName: 'Distribütör', valueGetter: ({ row }) => getVendorLabel(row.vendorId) },
        { headerName: 'Grup', valueGetter: ({ row }) => getGroupLabel(row.groupId) },
        { headerName: 'Satış Müdürü', valueGetter: ({ row }) => getSalesManagerLabel(row.salesManagerId) },
        { headerName: 'Temsilci Tipi', valueGetter: ({ row }) => getTypeLabel(row.representativeTypeId) },
        { headerName: 'Kullanıcı', valueGetter: ({ row }) => getUserLabel(row.id) },
        { headerName: 'Kullanıcı E-posta', valueGetter: ({ row }) => getUserEmail(row.id) },
        { headerName: 'Rut Tanımı', valueGetter: ({ row }) => getRouteLabel(row.id) },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[row.status]?.label ?? row.status ?? '') },
        { field: 'erpId', headerName: 'ERP Kodu' },
        { field: 'reference', headerName: 'Referans' },
        { field: 'plaka', headerName: 'Plaka' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `temsilciler_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma === (Distribütör, kod ile eşlenir)
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const importRowToPayload = (row) => {
    const vendorCode = importPick(row, ['Distribütör Kodu', 'Distributor Kodu', 'Tedarikçi Kodu', 'Tedarikci Kodu', 'Tedarikçi', 'Distribütör', 'vendorTextId']);
    const vendor = vendors.find((v) => (v.textId || '').toLowerCase() === vendorCode.toLowerCase());
    const typeText = importPick(row, ['Temsilci Tipi', 'Temsilci Tip', 'representativeType', 'Çalışma Tipi', 'Calisma Tipi']);
    const type = typeText
      ? typesAll.find((t) => (t.name || '').toLowerCase() === typeText.toLowerCase() || String(t.code ?? '') === typeText)
      : null;
    return {
      textId: importPick(row, ['Kod', 'textId', 'TextId']),
      name: importPick(row, ['Ad', 'Adı', 'name', 'Name']),
      representativeTypeId: type?.id || null,
      erpId: importPick(row, ['ERP Kodu', 'erpId']),
      reference: importPick(row, ['Referans', 'reference']),
      plaka: importPick(row, ['Plaka', 'plaka']),
      vendorId: vendor?.id || null,
      _vendorCode: vendorCode,
      _typeText: typeText,
    };
  };
  const importValidateRow = (p) => {
    if (!p.textId) return 'Kod boş';
    if (!p.name) return 'Ad boş';
    if (!p.representativeTypeId) return p._typeText ? `Temsilci Tipi bulunamadı: ${p._typeText}` : 'Temsilci Tipi boş';
    if (!p.vendorId) return p._vendorCode ? `Distribütör bulunamadı: ${p._vendorCode}` : 'Distribütör Kodu boş';
    return null;
  };
  const importSubmitRow = async (p) => {
    const payload = { textId: p.textId, name: p.name, erpId: p.erpId || null, reference: p.reference || null, plaka: p.plaka || null, groupId: null, salesManagerId: null, status: 1, vendorId: p.vendorId, representativeTypeId: p.representativeTypeId };
    try {
      const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    exportToExcel([{ 'Kod': 'T001', 'Ad': 'Örnek Temsilci', 'Distribütör Kodu': 'V001', 'Temsilci Tipi': 'Saha', 'ERP Kodu': '', 'Referans': '', 'Plaka': '' }], null, 'temsilci_sablon');
  };

  const renderTabs = () => (
    <div style={tabStyles.headerRow}>
      <div style={tabStyles.tabBar}>
        {definitionTabs.map((tab) => (
          <button key={tab.key} type="button" style={{ ...tabStyles.tab, ...(activeDefinition === tab.key ? tabStyles.tabActive : {}) }} onClick={() => setActiveDefinition(tab.key)}>
            <RIcon name={tab.icon} size={15} /> {tab.label}
          </button>
        ))}
      </div>
      <div style={statStyles.row}>
        {statCards.map((c) => (
          <div key={c.label} style={statStyles.card}>
            <div style={{ ...statStyles.value, ...(c.tone === 'ok' ? statStyles.valueOk : c.tone === 'neutral' ? statStyles.valueNeutral : {}) }}>{c.value}</div>
            <div style={statStyles.label}>{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (activeDefinition === 'groups') return <div style={s.container}>{renderTabs()}<RepresentativeGroupsPage /></div>;
  if (activeDefinition === 'types') return <div style={s.container}>{renderTabs()}<RepresentativeTypesPage /></div>;

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod, ad, ERP veya referans ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      {view === 'table' && <ColumnChooser table={table} />}
      {view === 'table' && <MultiGroupPicker value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} />}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      {renderTabs()}
      <ListPageShell
        title="Temsilciler"
        subtitle="Temsilci kayıtlarını filtreleyin, sıralayın ve yönetin."
        actions={(
          <>
            <div className="rep-vtoggle">
              {[['card', 'grid', 'Kart'], ['table', 'list', 'Tablo'], ['chart', 'bars', 'Grafik']].map(([v, ic, lbl]) => (
                <button key={v} type="button" className={view === v ? 'is-on' : ''} title={lbl} aria-label={lbl} onClick={() => setView(v)}><RIcon name={ic} size={16} /></button>
              ))}
            </div>
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Temsilci</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={(view === 'card' || viewMode === 'chart' || isGrouping) ? null : footerNode}
      >
        {view === 'card' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <div className="rep-cardwrap" onScroll={handleCardScroll}>
              {rows.length === 0 && !loading ? (
                <div className="rep-empty"><RIcon name="idcard" size={22} /> Eşleşen temsilci yok</div>
              ) : (
                <>
                  <div className="rep-cardgrid">
                    {rows.map((r) => (
                      <RepCard
                        key={r.id}
                        r={r}
                        vendorLabel={getVendorLabel(r.vendorId)}
                        typeLabel={r.representativeTypeId ? getTypeLabel(r.representativeTypeId) : ''}
                        userLabel={getUserLabel(r.id)}
                        routeLabel={getRouteLabel(r.id)}
                        onView={() => openView(r)}
                        onEdit={() => openEdit(r)}
                        onDel={() => { setSelected(r); setDeleteConfirm(true); }}
                        canEdit={canEdit}
                        canDelete={canDelete}
                      />
                    ))}
                  </div>
                  {totalCount > 0 && (
                    <div className="rep-loadbar">
                      {loadingMore
                        ? `Yükleniyor… (${rows.length} / ${totalCount})`
                        : rows.length >= totalCount
                          ? `Tümü yüklendi · ${totalCount} kayıt`
                          : (
                            <>
                              <span>{rows.length} / {totalCount} kayıt</span>
                              <button type="button" className="rep-loadmore" onClick={loadMore}>Daha Fazla Yükle</button>
                            </>
                          )}
                    </div>
                  )}
                </>
              )}
            </div>
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <ChartView rows={rows} getGroupLabel={getGroupLabel} getVendorLabel={getVendorLabel} getTypeLabel={getTypeLabel} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            resolveFilterProps={resolveFilterProps}
            isGrouping={isGrouping}
            nestedGroupBuckets={nestedGroupBuckets}
            expandedGroups={expandedGroups}
            toggleGroupExpand={toggleGroupExpand}
            bucketCounts={bucketCounts}
            bucketLoadingByPath={bucketLoadingByPath}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openView}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.name}"</strong> temsilcisini silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} temsilci içe aktarıldı`); fetchData(); } }}
        title="Temsilci İçe Aktar"
        helpText="Zorunlu alanlar: Kod, Ad, Distribütör Kodu (mevcut bir distribütör koduyla eşleşmeli) ve Temsilci Tipi (tanımlı bir tip adı/kodu ile eşleşmeli)."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
